// src/components/BookTourStepper.jsx
// "Book this tour" — sequential, one-stop-at-a-time booking flow for an
// entire generated plan. Distinct from BookingModal (single winery, any
// time, any order): this walks every winery_stop leg in order, and for each
// one calls mcp-booking's live per-click feasibility engine
// (GET /booking/availability in trip-aware mode) fresh — never precomputed.
// Same endpoint, same code path, same tripRef as BookingModal's plan-aware
// "Book tasting" — see TourResultsPage.jsx, which derives one tripRef per
// (plan_id, departure_at) and passes it to both. No sign-in required — a
// departure_at (from the Plan page) is all this needs.
//
// Skipping a stop is allowed by design (see booking-slot-engine spec) — the
// dwell-time sum used by the live engine is built from real confirmed
// bookings, so a skipped stop naturally contributes 0 with no special-casing
// needed here either.

import React, { useState, useEffect, useCallback } from "react";
import { Wine, Clock, Users, Check, X, ChevronRight, Loader, SkipForward } from "lucide-react";
import { api } from "../services/api";

function fmtSlotDate(d) {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  } catch { return d; }
}

export default function BookTourStepper({ tour, planId, departureAt, tripRef, userEmail, userName, partySize = 2, onClose, onBooked }) {
  // Every winery-stop leg, keeping its original index into tour.legs —
  // that original index is exactly what the API's leg_index param expects,
  // and it's what the live engine uses to sum dwell-time for stops before it.
  const [steps] = useState(() =>
    (tour?.legs || [])
      .map((leg, i) => ({ leg, legIndex: i }))
      .filter(x => x.leg.is_winery_stop && x.leg.to_winery_id)
  );

  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("loading"); // loading | slots | no-slots | confirm | booked | error
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState(userName || "");
  const [email, setEmail] = useState(userEmail || "");
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState([]); // { legIndex, name, status: booked|skipped|unavailable, booking? }

  const current = steps[idx];

  const loadSlots = useCallback(async () => {
    if (!current) return;
    setPhase("loading"); setError(""); setSelected(null);
    try {
      const res = await api.getBookingAvailability(current.leg.to_winery_id, {
        planId, legIndex: current.legIndex, departureAt, tripRef, partySize,
      });
      if (res.feasible) {
        setCandidates(res.candidates || []);
        setPhase("slots");
      } else {
        setCandidates([]);
        setPhase("no-slots");
      }
    } catch (e) {
      setError(e.message || "Couldn't check availability for this stop.");
      setPhase("error");
    }
  }, [current, planId, departureAt, tripRef, partySize]);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  function recordAndAdvance(entry) {
    setResults(r => [...r, entry]);
    setIdx(i => i + 1);
  }

  function handleSkip() {
    recordAndAdvance({ legIndex: current.legIndex, name: current.leg.to, status: "skipped" });
  }

  async function handleConfirm() {
    if (!selected || !name || !email) return;
    setBooking(true); setError("");
    try {
      const res = await api.createBooking({
        winery_id: current.leg.to_winery_id,
        visit_date: selected.date,
        slot_time: selected.slot_time,
        party_size: partySize,
        user_name: name,
        user_email: email,
        plan_id: planId,
        trip_ref: tripRef,
      });
      recordAndAdvance({ legIndex: current.legIndex, name: current.leg.to, status: "booked", booking: res });
      if (onBooked) onBooked(current.legIndex);
    } catch (e) {
      setError(e.message || "Booking failed. Please try again.");
    } finally {
      setBooking(false);
    }
  }

  const S = {
    overlay: { position: "fixed", inset: 0, zIndex: 1100, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
    card: { background: "#EDF1E4", border: "1px solid #252525", borderRadius: 14, padding: 24, maxWidth: 440, width: "100%", maxHeight: "90vh", overflowY: "auto" },
    label: { fontSize: 10, fontWeight: 600, color: "#7A8E68", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 },
    input: { width: "100%", background: "#E8EDE0", border: "1px solid #222", borderRadius: 8, padding: "10px 14px", fontSize: 13, fontFamily: "'Inter',sans-serif", color: "#2C3820", outline: "none", boxSizing: "border-box" },
    primaryBtn: { padding: "11px", background: "#6B2737", border: "none", borderRadius: 8, color: "#DCE4EC", fontSize: 13, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 },
    secondaryBtn: { padding: "11px", background: "#E8EDE0", border: "1px solid #2A2A2A", borderRadius: 8, color: "#5A6E48", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 },
  };

  if (steps.length === 0) {
    return (
      <div style={S.overlay} onClick={onClose}>
        <div style={S.card} onClick={e => e.stopPropagation()}>
          <p style={{ fontSize: 13, color: "#5A6E48" }}>This tour has no bookable winery stops.</p>
          <button onClick={onClose} style={{ ...S.primaryBtn, width: "100%", marginTop: 12 }}>Close</button>
        </div>
      </div>
    );
  }

  // Final summary once every stop has been booked or skipped.
  if (idx >= steps.length) {
    const bookedCount = results.filter(r => r.status === "booked").length;
    return (
      <div style={S.overlay} onClick={onClose}>
        <div style={S.card} onClick={e => e.stopPropagation()}>
          <div style={{ textAlign: "center", padding: "12px 0 4px" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(74,112,82,0.15)", border: "1px solid rgba(74,112,82,0.3)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <Check size={24} color="#6AAA7A" />
            </div>
            <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 19, color: "#1E2C14", margin: "0 0 8px" }}>
              {bookedCount > 0 ? `${bookedCount} tasting${bookedCount === 1 ? "" : "s"} requested` : "All stops reviewed"}
            </h3>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
            {results.map((r, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderRadius: 7, background: "#E2E8D8", border: "1px solid #222" }}>
                <span style={{ fontSize: 12.5, color: "#2C3820", fontWeight: 500 }}>{r.name}</span>
                <span style={{ fontSize: 11, color: r.status === "booked" ? "#4A7052" : "#886070" }}>
                  {r.status === "booked" ? `✓ ${fmtSlotDate(r.booking.visit_date)} · ${r.booking.slot_time}` : r.status === "skipped" ? "Skipped" : "No slots available"}
                </span>
              </div>
            ))}
          </div>
          <button onClick={onClose} style={{ ...S.primaryBtn, width: "100%" }}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.card} onClick={e => e.stopPropagation()}>
        <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: "#7A8E68", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>
              Stop {idx + 1} of {steps.length}
            </div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 17, color: "#1E2C14", display: "flex", alignItems: "center", gap: 6 }}>
              <Wine size={14} color="#886070" />{current.leg.to}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#5A6E48", cursor: "pointer", fontSize: 18 }}>✕</button>
        </div>

        {error && (
          <div style={{ background: "rgba(107,39,55,0.1)", border: "1px solid rgba(107,39,55,0.2)", borderRadius: 7, padding: "8px 12px", marginBottom: 16, fontSize: 12, color: "#886070" }}>
            {error}
          </div>
        )}

        {phase === "loading" && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#6A7E58", fontSize: 13, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            <Loader size={18} style={{ animation: "spin 1s linear infinite" }} />
            Checking availability…
          </div>
        )}

        {phase === "no-slots" && (
          <>
            <div style={{ textAlign: "center", padding: "24px 0", color: "#886070", fontSize: 13, fontWeight: 500 }}>
              No slots available for the booking
            </div>
            <button onClick={handleSkip} style={{ ...S.primaryBtn, width: "100%" }}>
              <SkipForward size={13} /> Skip and continue
            </button>
          </>
        )}

        {phase === "error" && (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={loadSlots} style={{ ...S.secondaryBtn, flex: 1 }}>Retry</button>
            <button onClick={handleSkip} style={{ ...S.primaryBtn, flex: 1 }}><SkipForward size={13} /> Skip</button>
          </div>
        )}

        {phase === "slots" && (
          <>
            <div style={S.label}>Available times</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto", marginBottom: 16 }}>
              {candidates.map((slot) => {
                const sel = selected?.id === slot.id;
                return (
                  <button key={slot.id} onClick={() => setSelected(slot)} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 14px", borderRadius: 8,
                    border: sel ? "1px solid #3A2028" : "1px solid #222",
                    background: sel ? "#E2D8DA" : "#EDF1E4",
                    cursor: "pointer",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Clock size={13} color={sel ? "#886070" : "#6A7E58"} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: sel ? "#C48890" : "#2C3820" }}>
                        {fmtSlotDate(slot.date)} · {slot.slot_time}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Users size={11} color="#6A7E58" />
                      <span style={{ fontSize: 11, color: "#5A6E48" }}>{slot.remaining} left</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {selected && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                <input style={S.input} placeholder="Your name" value={name} onChange={e => setName(e.target.value)} />
                <input style={S.input} placeholder="Email address" type="email" value={email} onChange={e => setEmail(e.target.value)} />
              </div>
            )}

            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleSkip} style={{ ...S.secondaryBtn, flex: 1 }}>
                <SkipForward size={13} /> Skip
              </button>
              <button onClick={handleConfirm} disabled={!selected || !name || !email || booking} style={{
                ...S.primaryBtn, flex: 2,
                background: selected && name && email && !booking ? "#6B2737" : "#EAEFE2",
                color: selected && name && email && !booking ? "#DCE4EC" : "#7A8E68",
                cursor: selected && name && email && !booking ? "pointer" : "not-allowed",
              }}>
                {booking ? <><Loader size={13} style={{ animation: "spin 1s linear infinite" }} /> Booking…</> : <>Book this stop <ChevronRight size={13} /></>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
